import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AuthProvider } from '@/contexts/AuthContext';
import { PackageProvider } from '@/contexts/PackageContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { TimezoneProvider } from '@/contexts/TimezoneContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { SSEProvider } from '@/contexts/SSEContext';
import { OnboardingProvider } from '@/contexts/OnboardingContext';
import { ModalStackProvider } from '@/contexts/ModalStackContext';
import { SetupWizard, FloatingChecklist } from '@/components/onboarding';
import { OnboardingActions } from '@/components/onboarding/OnboardingActions';
import { PrivateRoute } from '@/components/PrivateRoute';
import { RequireBrandContext } from '@/components/routing/RequireBrandContext';
import { RequireEntitlement } from '@/components/routing/RequireEntitlement';
import { BlogListHeader, BlogNewHeader, BlogDetailLockedHeader } from '@/components/blog/BlogPageHeaders';
import { BrandCheckPageHeader } from '@/components/brandcheck/BrandCheckPageHeader';
import { DashboardLayout } from '@/components/layout';
import { TooltipProvider } from '@/components/ui/Tooltip';
import { ToastContainer } from '@/components/ui/ToastContainer';
import { LastReviewerConfirmHost } from '@/components/release/LastReviewerConfirmHost';
import { GatingHost } from '@/components/gating/GatingHost';
import { ReleaseUndoSnackbar } from '@/components/release/ReleaseUndoSnackbar';
import { ReleaseMoment } from '@/components/release/ReleaseMoment';
import { CrispChat } from '@/components/CrispChat';
import { Login } from '@/pages/Login';
import { MagicLoginLanding } from '@/pages/MagicLoginLanding';
import { Register } from '@/pages/Register';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ResetPassword } from '@/pages/ResetPassword';
import { WelcomePage } from '@/pages/WelcomePage';
import { InvitationAccept } from '@/pages/InvitationAccept';
import { VerifyEmail } from '@/pages/VerifyEmail';
import { Dashboard } from '@/pages/Dashboard';
import { Calendar } from '@/pages/Calendar';
import { Creator } from '@/pages/Creator';
import { BrandCheck } from '@/pages/BrandCheck';
import { Analytics } from '@/pages/Analytics';
import { Settings } from '@/pages/Settings';
import { Profile } from '@/pages/Profile';
import { NotFound } from '@/pages/NotFound';
import { ApprovalBoard } from '@/pages/ApprovalBoard';
import { NotificationsPage } from '@/pages/Notifications';
import { AgencyPortfolio } from '@/pages/AgencyPortfolio';
import { AgencySettings } from '@/pages/AgencySettings';
import { AgencyWhitelabelSettings } from '@/pages/AgencyWhitelabelSettings';
import { WhitelabelSettings } from '@/pages/WhitelabelSettings';
import { MediaLibrary } from '@/pages/MediaLibrary';
import { Blog } from '@/pages/Blog';
import { BlogNew } from '@/pages/BlogNew';
import { BlogDetail } from '@/pages/BlogDetail';
import { QuickHub } from '@/pages/QuickHub';
import { Help } from '@/pages/Help';
import { Status } from '@/pages/Status';
import { DesignSystem } from '@/pages/DesignSystem';
import { Variations } from '@/pages/Variations';
import { ChannelSelectPage } from '@/pages/channels/ChannelSelectPage';
import { RequireSuperAdmin } from '@/components/RequireSuperAdmin';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { AdminDashboard } from '@/pages/admin';
import { AdminUsers } from '@/pages/admin/Users';
import { AdminUserDetail } from '@/pages/admin/UserDetail';
import { AdminTeams } from '@/pages/admin/Teams';
import { AdminTeamDetail } from '@/pages/admin/TeamDetail';
import { AdminAgencies } from '@/pages/admin/Agencies';
import { AdminAgencyDetail } from '@/pages/admin/AgencyDetail';
import { AdminPackages } from '@/pages/admin/Packages';
import { AdminAffiliates } from '@/pages/admin/Affiliates';
import { AdminAffiliateDetail } from '@/pages/admin/AffiliateDetail';
import { AdminNotifications } from '@/pages/admin/Notifications';
import { AdminSystem } from '@/pages/admin/System';
import { AdminBranding } from '@/pages/admin/Branding';
import { AdminHelp } from '@/pages/admin/Help';
import { StatusAdmin } from '@/pages/admin/StatusAdmin';
import { Events as AdminEvents } from '@/pages/admin/Events';
import { SystemNotificationBanner } from '@/components/SystemNotificationBanner';
import { ReconnectBanner } from '@/components/ReconnectBanner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { isNonRetryableError, isRateLimitError, getRetryAfterMs } from '@/services/api';
import { useStripAutoReloadParam } from '@/hooks/useStripAutoReloadParam';
import { useUnsavedChangesStore } from '@/stores/unsavedChangesStore';
import { scanDomUnsavedInput } from '@/utils/scanDomUnsavedInput';
import { devLog } from '@/utils/logger';
import '@/i18n'; // Initialize i18n

const ShopPage = lazy(() => import('@/pages/ShopPage').then((m) => ({ default: m.ShopPage })));
const ShopCheckoutPage = lazy(() => import('@/pages/ShopCheckoutPage').then((m) => ({ default: m.ShopCheckoutPage })));
const EarlySupporterReturnPage = lazy(() =>
  import('@/pages/EarlySupporterReturnPage').then((m) => ({ default: m.EarlySupporterReturnPage })),
);

/**
 * Smart retry function with exponential backoff
 * - Stops immediately on non-retryable errors (4xx except 429)
 * - Uses exponential backoff: 1s, 2s, 4s
 * - Respects rate limit headers
 * - Maximum 3 retries
 */
const smartRetry = (failureCount: number, error: unknown): boolean => {
  // Max 3 retries
  if (failureCount >= 3) {
    devLog.warn('[QueryClient] Max retries reached, giving up');
    return false;
  }

  // Check if it's an Axios error
  if (error instanceof Error && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;

    // Don't retry non-retryable errors (4xx except 429)
    if (isNonRetryableError(axiosError)) {
      devLog.warn('[QueryClient] Non-retryable error, not retrying:', axiosError.response?.status);
      return false;
    }

    // For rate limit errors, log and allow retry (with delay handled by retryDelay)
    if (isRateLimitError(axiosError)) {
      devLog.warn('[QueryClient] Rate limited, will retry after delay');
      return true;
    }
  }

  // Default: retry for network errors and 5xx
  devLog.log(`[QueryClient] Retrying (attempt ${failureCount + 1})...`);
  return true;
};

/**
 * Smart retry delay with exponential backoff
 * - Base delay: 1 second
 * - Exponential: 1s, 2s, 4s
 * - Jitter: +/- 20% to prevent thundering herd
 * - Respects retry-after header for rate limits
 */
const smartRetryDelay = (attemptIndex: number, error: unknown): number => {
  // Check for rate limit with retry-after header
  if (error instanceof Error && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    if (isRateLimitError(axiosError)) {
      const retryAfter = getRetryAfterMs(axiosError);
      devLog.log(`[QueryClient] Rate limit retry delay: ${retryAfter}ms`);
      return retryAfter;
    }
  }

  // Exponential backoff with jitter
  const baseDelay = 1000; // 1 second
  const exponentialDelay = baseDelay * Math.pow(2, attemptIndex);
  const jitter = exponentialDelay * 0.2 * (Math.random() - 0.5); // +/- 10%
  const finalDelay = Math.min(exponentialDelay + jitter, 30000); // Cap at 30 seconds

  devLog.log(`[QueryClient] Retry delay: ${Math.round(finalDelay)}ms (attempt ${attemptIndex + 1})`);
  return finalDelay;
};

/**
 * React Query Client Configuration
 *
 * Cache settings optimized for real-time SaaS experience:
 * - staleTime: 1 minute - balance between freshness and API calls
 * - refetchOnMount: true - ensures data refreshes when navigating
 * - Individual hooks can override with shorter staleTime (e.g., dashboard: 30s)
 *
 * Team-scoped caching:
 * - All team data uses ['team', teamId, ...] query keys
 * - Cache automatically isolated per team
 * - God Mode switches handled by query key changes
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute default (was 5 min)
      gcTime: 10 * 60 * 1000, // Keep 10 min cache GC
      retry: smartRetry, // Keep smart retry
      retryDelay: smartRetryDelay, // Keep smart retry delay
      refetchOnWindowFocus: true, // Refetch stale data when user returns to tab (handles overnight stale media URLs)
      refetchOnReconnect: 'always', // Keep: Refetch when reconnecting
      refetchOnMount: true, // CHANGED: Refetch on navigation for freshness
      networkMode: 'offlineFirst', // Keep for PWA support
    },
    mutations: {
      retry: 0, // Don't retry failed mutations (they modify data)
      networkMode: 'offlineFirst',
    },
  },
});

/**
 * Preserves incoming query params (success, error, channelId, etc.) from OAuth
 * callbacks that land on /settings/channels, then merges tab=channels before
 * redirecting to /settings.  The plain <Navigate> it replaces hardcoded its
 * own query string and discarded everything the OAuth provider sent.
 */
function SettingsChannelsRedirect(): JSX.Element {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', 'channels');
  return <Navigate to={`/settings?${params.toString()}`} replace />;
}

/**
 * App Component
 *
 * Main application component with routing
 * Provider hierarchy: QueryClient > ErrorBoundary > Suspense > Language > Tooltip > Auth > Package > Timezone > Theme > SSE > Router
 */

function App() {
  // Signal to the stuck-load watchdog in index.html that React has actually
  // mounted, so it never shows its "taking longer than expected" recovery
  // overlay on a normal load. Fires after the first commit — see
  // src/types/global.d.ts for the full contract.
  useEffect(() => {
    window.__appMounted = true;
    // A slow first load can mount AFTER the watchdog's 25 s: cancel a still-pending timer and remove an overlay it
    // already showed, so a fully rendered app is never left blocked under "taking longer than expected".
    if (window.__appStuckTimer !== undefined) {
      clearTimeout(window.__appStuckTimer);
      window.__appStuckTimer = undefined;
    }
    document.getElementById('app-stuck-overlay')?.remove();
  }, []);

  // Silently strip ?autoreload=true left by the auto-reload path in the
  // inline PWA update script (see index.html).
  useStripAutoReloadParam();

  // Expose an unsaved-work check to the vanilla inline auto-reload script
  // (index.html), which runs before this bundle loads and cannot import
  // React state directly. Two layers:
  // 1. useUnsavedChangesStore — precise, opt-in registry populated by
  //    editors that call useUnsavedGuard (e.g. PostCreator).
  // 2. scanDomUnsavedInput — generic DOM fallback so un-wired forms are
  //    still protected (open dialogs with filled text, any password
  //    field with a value). Belt-and-suspenders for forms that haven't
  //    been wired up to the registry yet.
  useEffect(() => {
    window.__hasUnsavedChanges = () =>
      useUnsavedChangesStore.getState().hasUnsavedChanges() || scanDomUnsavedInput();

    return () => {
      delete window.__hasUnsavedChanges;
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Suspense fallback={<div className="flex items-center justify-center h-screen text-gray-600">Loading...</div>}>
          {/* PWA Update Banner is handled by inline script in index.html (no React dependency) */}
          <LanguageProvider>
            <TooltipProvider delayDuration={400} skipDelayDuration={300}>
            <ModalStackProvider>
            <AuthProvider>
            <OnboardingProvider>
            <PackageProvider>
            <TimezoneProvider>
            <ThemeProvider>
            <SSEProvider>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
          <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/magic" element={<MagicLoginLanding />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password/:token" element={<ResetPassword />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/Invitation/accept/:token" element={<InvitationAccept />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/design-system" element={<DesignSystem />} />
          <Route path="/status" element={<Status />} />

          {/* Channel Selection for any platform (after OAuth) */}
          <Route path="/channels/:platform/select" element={<ChannelSelectPage />} />

          {/* Protected Routes with shared layout */}
          <Route
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/quick/hub" element={<QuickHub />} />
            <Route path="/calendar" element={<RequireBrandContext><Calendar /></RequireBrandContext>} />
            <Route path="/creator" element={<RequireBrandContext><Creator /></RequireBrandContext>} />
            <Route path="/brand-check" element={<RequireBrandContext><RequireEntitlement feature="brand_check" header={<BrandCheckPageHeader />}><BrandCheck /></RequireEntitlement></RequireBrandContext>} />
            <Route path="/analytics" element={<RequireBrandContext><Analytics /></RequireBrandContext>} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings/channels" element={<SettingsChannelsRedirect />} />
            <Route path="/approvals" element={<ApprovalBoard />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/checkout" element={<ShopCheckoutPage />} />
            <Route path="/upgrade/early-supporter" element={<Navigate to="/shop" replace />} />
            <Route path="/upgrade/early-supporter/return" element={<EarlySupporterReturnPage />} />
            {/* C&C retired — Portfolio is the canonical agency landing.
                Redirect /agency and the legacy /agency/cc URL to Portfolio so
                bookmarks and external links keep working. */}
            <Route path="/agency" element={<Navigate to="/agency/portfolio" replace />} />
            <Route path="/agency/cc" element={<Navigate to="/agency/portfolio" replace />} />
            <Route path="/agency/portfolio" element={<AgencyPortfolio />} />
            <Route path="/agency/settings" element={<AgencySettings />} />
            <Route path="/agency/settings/whitelabel" element={<AgencyWhitelabelSettings />} />
            <Route path="/settings/whitelabel" element={<RequireBrandContext redirectTo="/agency/settings/whitelabel"><WhitelabelSettings /></RequireBrandContext>} />
            <Route path="/media-library" element={<RequireBrandContext><MediaLibrary /></RequireBrandContext>} />
            <Route path="/blog" element={<RequireBrandContext><RequireEntitlement feature="wordpress" header={<BlogListHeader />}><Blog /></RequireEntitlement></RequireBrandContext>} />
            <Route path="/blog/new" element={<RequireBrandContext><RequireEntitlement feature="wordpress" header={<BlogNewHeader />}><BlogNew /></RequireEntitlement></RequireBrandContext>} />
            <Route path="/blog/:id" element={<RequireBrandContext><RequireEntitlement feature="wordpress" header={<BlogDetailLockedHeader />}><BlogDetail /></RequireEntitlement></RequireBrandContext>} />
            <Route path="/variations" element={<RequireBrandContext><Variations /></RequireBrandContext>} />
            <Route path="/help" element={<Help />} />
          </Route>

          {/* Super Admin Routes */}
          <Route
            path="/admin"
            element={
              <RequireSuperAdmin>
                <AdminLayout />
              </RequireSuperAdmin>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="teams" element={<AdminTeams />} />
            <Route path="teams/:id" element={<AdminTeamDetail />} />
            <Route path="agencies" element={<AdminAgencies />} />
            <Route path="agencies/:id" element={<AdminAgencyDetail />} />
            <Route path="packages" element={<AdminPackages />} />
            <Route path="affiliates" element={<AdminAffiliates />} />
            <Route path="affiliates/:id" element={<AdminAffiliateDetail />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="system" element={<AdminSystem />} />
            <Route path="branding" element={<AdminBranding />} />
            <Route path="help" element={<AdminHelp />} />
            <Route path="status" element={<StatusAdmin />} />
            <Route path="events" element={<AdminEvents />} />
          </Route>

          {/* Catch all - 404 */}
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
        {/* Setup Wizard - Shows for new users */}
        <SetupWizard />
        {/* Onboarding Actions - Handles checklist action events */}
        <OnboardingActions />
        {/* Floating Checklist - Shows onboarding progress */}
        <FloatingChecklist />
        {/* Plan gating: upsell modal (403 FEATURE_NOT_AVAILABLE) + AI-token modal. Inside the router
            (LockedFeatureCard navigates) and inside Tooltip/ModalStack/Auth/Package providers. */}
        <GatingHost />
          </BrowserRouter>
          {/* System Notification Banner - Shows critical system notifications */}
          <SystemNotificationBanner />
          {/* Reconnect Banner - Non-destructive mid-session indicator when the
              token-refresh circuit breaker opens (see api.ts / AuthContext) */}
          <ReconnectBanner />
          {/* Release undo snackbar (bottom-centre, survives the drawer closing) */}
          <ReleaseUndoSnackbar />
          <ReleaseMoment />
          {/* Last-reviewer confirmation: needs Tooltip/ModalStack providers, so it lives inside them */}
          <LastReviewerConfirmHost />
          {/* Crisp Chat Widget - Inside AuthProvider for user context */}
          <CrispChat />
          </SSEProvider>
          </ThemeProvider>
          </TimezoneProvider>
          </PackageProvider>
          </OnboardingProvider>
          </AuthProvider>
          </ModalStackProvider>
          </TooltipProvider>

          {/* Toast Notifications - Global */}
          <ToastContainer />
        </LanguageProvider>
      </Suspense>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
